/* eslint-disable no-useless-catch */
import { defineStore } from 'pinia';
import CreateMailoutApi from '@/api';
import type {
  FlattenPdfRequest,
  FlattenPdfResponse,
  PdfGeneratePresignedRequest,
  PdfGeneratePresignedResponse,
  PdfPreviewRequest,
  PdfValidateRequest,
  PdfValidateApiResponse,
} from '@/types/pdf';
import type { PnUploadFileItem } from '@postnord/web-components/types/globals/types';
import { useTopbarStore } from '@/store/topbar';
import config from '@/config';
import { PdfDraftData } from '@/types/myOrders';

export const usePdfStore = defineStore('pdf', {
  state: () => ({
    file: null as File | null,
    generatePresignedData: null as PdfGeneratePresignedResponse | null,
    validateData: null as PdfValidateApiResponse | null,
    flattenPdfData: null as FlattenPdfResponse | null,
    pdfFileName: '',
    pdfFileSize: '',
    pdfFileNumberOfPages: '',
    pdfFileWidth: '',
    pdfFileHeight: '',
    isFileSizeValid: false,
    isPageFormatValid: false,
    isNumberOfPagesValid: false,
    pdfPreviewUrl: '',
    postcardName: '',
    pdfProcessedKey: '',
  }),
  actions: {
    setFile(fileInput: PnUploadFileItem) {
      this.file = fileInput instanceof File ? fileInput : fileInput.file;
    },
    setPostcardName(name: string) {
      this.postcardName = name;
    },
    setPdfPreviewUrl(url: string) {
      this.pdfPreviewUrl = url;
    },
    clearPdfData(clearPostcardName = true) {
      this.file = null;
      this.generatePresignedData = null;
      this.validateData = null;
      this.flattenPdfData = null;
      this.pdfFileName = '';
      this.pdfFileSize = '';
      this.pdfFileNumberOfPages = '';
      this.pdfFileWidth = '';
      this.pdfFileHeight = '';
      this.isFileSizeValid = false;
      this.isPageFormatValid = false;
      this.isNumberOfPagesValid = false;
      this.pdfPreviewUrl = '';
      this.pdfProcessedKey = '';
      if (clearPostcardName) {
        this.postcardName = '';
      }
    },
    restoreFromDraft(data: PdfDraftData) {
      this.pdfFileName = data.fileName;
      this.postcardName = data.postcardName;
      this.pdfProcessedKey = data.processedKey || '';
      if (data.pdfFileNumberOfPages) {
        this.pdfFileNumberOfPages = data.pdfFileNumberOfPages;
      }
    },
    async uploadPdf(file: PnUploadFileItem): Promise<string> {
      this.pdfFileName = file instanceof File ? file.name : (file.filename ?? '');
      this.flattenPdfData = null;
      try {
        const presigned = await this.postPdfGeneratePresigned(file);
        if (!presigned?.url || !presigned?.key) {
          throw new Error('postPdfGeneratePresigned returned an invalid response');
        }

        await CreateMailoutApi.uploadFileToPresignedUrl(presigned.url, this.file as File);

        const validateResponse = await this.postPdfValidate(presigned.key);
        const finalProcessedKey = this.pdfProcessedKey || validateResponse.processedKey;
        this.pdfProcessedKey = finalProcessedKey;
        const previewUrl = await this.postPdfPreview(finalProcessedKey);

        return previewUrl;
      } catch (e: any) {
        throw e;
      }
    },
    async postPdfGeneratePresigned(file: PnUploadFileItem): Promise<PdfGeneratePresignedResponse> {
      try {
        const name = file instanceof File ? file.name : file?.filename;
        const contentLength = file instanceof File ? file.size : file?.filesize;
        const payload: PdfGeneratePresignedRequest = {
          name,
          contentLength,
        };
        const response = (await CreateMailoutApi.postPdfGeneratePresigned(
          payload
        )) as PdfGeneratePresignedResponse;
        this.generatePresignedData = response;
        return response;
      } catch (e: any) {
        throw e;
      }
    },
    async postPdfValidate(pdfFolder: string): Promise<PdfValidateApiResponse> {
      try {
        const payload: PdfValidateRequest = {
          key: pdfFolder,
        };
        const response = (await CreateMailoutApi.postPdfValidate(
          payload
        )) as PdfValidateApiResponse;
        this.validateData = response;
        this.pdfProcessedKey = response.processedKey || '';

        if (response?.sizeLimit?.current !== undefined) {
          this.pdfFileSize = String(response.sizeLimit.current);
        }
        if (response?.pageLimit?.current !== undefined) {
          this.pdfFileNumberOfPages = String(response.pageLimit.current);
        }
        if (response?.resolution?.detectedFormat?.width !== undefined) {
          this.pdfFileWidth = String(response.resolution.detectedFormat.width);
        }
        if (response?.resolution?.detectedFormat?.height !== undefined) {
          this.pdfFileHeight = String(response.resolution.detectedFormat.height);
        }

        if (!response?.allValid) {
          throw new Error(response?.message);
        }

        if (response?.flatteningRequired) {
          const flattenResponse = await this.flattenPdf(pdfFolder, response?.flattenReasons);
          this.pdfProcessedKey = flattenResponse.processedKey || this.pdfProcessedKey;
        }

        return response;
      } catch (e: any) {
        throw e;
      }
    },

    async postPdfPreview(key: string): Promise<string> {
      const topBarStore = useTopbarStore();
      try {
        const payload: PdfPreviewRequest = {
          key,
          service: config.VUE_DEFAULT_SERVICE,
          language: topBarStore.topBar?.language || config.VUE_DEFAULT_LANGUAGE,
        };
        const response = await CreateMailoutApi.postPdfPreview(payload);
        this.validateData = response;

        return response.downloadUrl;
      } catch (e: any) {
        throw e;
      }
    },

    async flattenPdf(
      pdfFolder: string,
      flattenReasons: FlattenPdfRequest['flattenReasons']
    ): Promise<FlattenPdfResponse> {
      try {
        const payload: FlattenPdfRequest = {
          key: pdfFolder,
          flattenReasons,
        };
        const response: FlattenPdfResponse = await CreateMailoutApi.postFlattenPdf(payload);
        if (response?.status !== 'flattened') {
          throw new Error(response?.message);
        }
        this.flattenPdfData = response;
        this.pdfProcessedKey = response.processedKey || this.pdfProcessedKey;
        return response;
      } catch (e: any) {
        throw e;
      }
    },
  },
});
